import {
  Bell,
  CalendarPlus,
  ClipboardCheck,
  Inbox,
  MessageSquare,
  MessageSquareReply,
  Repeat,
  ShieldAlert,
  UserMinus,
  UserPlus,
} from "lucide-react";
import { PRIORITY_NOT_SET, normalizePriority } from "./eventPriority";

/* =============================================================
   Where a notification came from, and how to say it in a line.

   The notification record carries no sender: `createdBy` is "NA" on
   every row the API writes (see notification.service.js create()). So
   "from" is worked out here, once, for the page and the toast both --
   if the two worked it out separately a toast could say one thing and
   the list it opens onto another.

   `key` is what the page groups a stack by. It is the source, not the
   raw type: "added to a batch" and "removed from a batch" are one
   stream to the reader, the way one app's notifications are one stack.
   ============================================================= */

const SOURCES = {
  event_created: { key: "events", label: "Events", icon: CalendarPlus },
  attendance_marked: { key: "attendance", label: "Attendance", icon: ClipboardCheck },
  batch_added: { key: "batches", label: "Batches", icon: UserPlus },
  batch_removed: { key: "batches", label: "Batches", icon: UserMinus },
  chat_message: { key: "messages", label: "Messages", icon: MessageSquare },
  feedback_reply: { key: "feedback", label: "Feedback", icon: MessageSquareReply },
  feedback_received: { key: "feedback", label: "Feedback", icon: Inbox },
  action_failed: { key: "system", label: "System", icon: ShieldAlert },
  permission_denied: { key: "system", label: "System", icon: ShieldAlert },
  error_digest: { key: "system", label: "System", icon: ShieldAlert },
};

/* A batch change request and its verdict: `switch_requested`,
   `switch_approved`, `switch_rejected`, `switch_reverted` and the
   `join_*` set the same code writes when the student has no batch to
   leave (batchchangerequest.service.js). One stream to the reader, and
   named here by prefix because the verdicts are built as
   `${requestType}_${outcome}` rather than written out. Without the
   `join_` half these fell through to "SevenMentor LMS". */
const CHANGE_REQUEST_PREFIXES = ["switch_", "join_"];
const SWITCH_SOURCE = { key: "switch", label: "Batch change", icon: Repeat };
const GENERAL_SOURCE = { key: "general", label: "SevenMentor LMS", icon: Bell };

export const getNotificationSource = (n) => {
  const type = String(n?.type || "");
  if (SOURCES[type]) return SOURCES[type];
  if (CHANGE_REQUEST_PREFIXES.some((p) => type.startsWith(p))) return SWITCH_SOURCE;
  return GENERAL_SOURCE;
};

/* Two kinds name a person in their title, in a fixed form the API
   writes (chat.notify.js, studentfeedback.notify.js). Those give the
   person. Everything else is from its batch when it has one, and from
   its source when it does not. */
const PERSON_IN_TITLE = {
  chat_message: /^New message from (.+)$/i,
  feedback_reply: /^(.+?) replied to your feedback$/i,
};

export const getNotificationFrom = (n) => {
  const pattern = PERSON_IN_TITLE[String(n?.type || "")];
  const match = pattern ? String(n?.title || "").trim().match(pattern) : null;
  if (match && match[1].trim()) return match[1].trim();
  if (n?.batchName) return n.batchName;
  return getNotificationSource(n).label;
};

/** "high" | "medium" | "low" | "none" -- the class suffix both surfaces use. */
export const getPriorityTone = (priority) => {
  const p = normalizePriority(priority);
  if (p === "High") return "high";
  if (p === "Medium") return "medium";
  if (p === "Low") return "low";
  return "none";
};

/** The priority's label, or "" when it was never set. */
export const getPriorityLabel = (priority) => {
  const p = normalizePriority(priority);
  return p === PRIORITY_NOT_SET ? "" : p;
};

/* How an attendance notification's recipient was marked, as the API wrote
   it (notification.attendanceStatus). Null for every other type, for a
   common-off day and for notifications sent before the field existed --
   those keep the ordinary look rather than guessing. */
export const ATTENDANCE_STATUS = {
  present: { label: "Present", color: "#0E7C6B", tint: "#E4F4F0", pill: "pgm-pill--good" },
  absent: { label: "Absent", color: "#B3202E", tint: "#FDECEE", pill: "pgm-pill--bad" },
};

export const getAttendanceStatus = (n) => {
  if (String(n?.type || "") !== "attendance_marked") return null;
  return ATTENDANCE_STATUS[n?.attendanceStatus] ? n.attendanceStatus : null;
};

/* The message, in a line or two. A feedback reply keeps its context in
   `message` and the reply itself in `replyText`; the context is the
   better summary, so the reply is only the fallback. Cut on a word, not
   through one. */
export const summarizeNotification = (n, max = 140) => {
  const text = String(n?.message || n?.replyText || "")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
};
